import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  Chip,
  Card,
  CardActionArea,
} from '@mui/material';
import {
  Search,
  MedicalServices,
  Inventory,
  Store,
  ArrowForward,
  AccessTime,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../lib/axios';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RecentProduct {
  id: string;
  name: string;
  chainName: string;
  createdAt: string;
}

interface CatalogStats {
  totalProducts: number;
  totalInStock: number;
  totalBranches: number;
  recentProducts: RecentProduct[];
  lastSyncedAt: string | null;
  computedAt: string;
}

// ─── Colors ───────────────────────────────────────────────────────────────────

const NAVY = '#0D1B35';
const BLUE = '#0066CC';

// ─── Subcomponents ────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  value: number | undefined;
  label: string;
  icon: React.ReactNode;
  loading: boolean;
}> = ({ value, label, icon, loading }) => (
  <Card
    elevation={0}
    sx={{
      flex: '0 0 140px',
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    }}
  >
    <Box sx={{ color: BLUE }}>{icon}</Box>
    {loading ? (
      <Skeleton width={60} height={36} />
    ) : (
      <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: NAVY, lineHeight: 1 }}>
        {value?.toLocaleString() ?? '—'}
      </Typography>
    )}
    <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>
      {label}
    </Typography>
  </Card>
);

const MedCard: React.FC<{ product: RecentProduct; onClick: () => void }> = ({
  product,
  onClick,
}) => (
  <Card
    elevation={0}
    sx={{
      flex: '0 0 200px',
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      overflow: 'hidden',
    }}
  >
    <CardActionArea onClick={onClick} sx={{ p: 2, height: '100%', alignItems: 'flex-start', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          bgcolor: 'rgba(0,102,204,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 0.5,
        }}
      >
        <MedicalServices sx={{ fontSize: 16, color: BLUE }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 13.5,
          color: NAVY,
          lineHeight: 1.3,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {product.name}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.3 }}>
        {product.chainName}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto', pt: 1 }}>
        <AccessTime sx={{ fontSize: 11, color: 'text.disabled' }} />
        <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>
          {new Date(product.createdAt).toLocaleDateString()}
        </Typography>
      </Box>
    </CardActionArea>
  </Card>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export const DoctorDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: stats, isLoading, isError } = useQuery<CatalogStats>({
    queryKey: ['catalog-stats'],
    queryFn: async () => {
      const { data } = await apiClient.get<CatalogStats>('/products/catalog-stats');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: activePromotions = [], isLoading: isPromosLoading } = useQuery<{
    id: string;
    productName: string;
    description: string;
    backgroundColor: string;
  }[]>({
    queryKey: ['active-promotions'],
    queryFn: async () => {
      const { data } = await apiClient.get('/promotions/active');
      return data;
    },
  });

  // Carousel index for multiple promotions
  const [promoIndex, setPromoIndex] = useState(0);

  useEffect(() => {
    if (activePromotions.length <= 1) return;
    const timer = setInterval(() => {
      setPromoIndex((prev) => (prev + 1) % activePromotions.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [activePromotions.length]);

  const activePromotion = activePromotions.length > 0 ? activePromotions[promoIndex % activePromotions.length] : null;

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return t('doctor.dashboard.greeting_morning');
    if (hour < 18) return t('doctor.dashboard.greeting_afternoon');
    return t('doctor.dashboard.greeting_evening');
  }, [t]);

  const doctorName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.firstName ?? user?.email?.split('@')[0] ?? '';

  const recentSearches: string[] = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('med-recent-searches') || '[]');
    } catch {
      return [];
    }
  }, []);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', width: '100%', minWidth: 0 }}>

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      {isPromosLoading ? (
        <Skeleton
          variant="rounded"
          sx={{
            borderRadius: 4,
            height: { xs: 220, sm: 240 },
            mb: 3,
          }}
        />
      ) : activePromotion ? (
        <Box
          sx={{
            borderRadius: 4,
            bgcolor: activePromotion.backgroundColor,
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            p: { xs: '28px 24px', sm: '36px 40px' },
            mb: 3,
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'background-color 0.9s ease-in-out',
          }}
          onClick={() => navigate(`/doctor/medications?q=${encodeURIComponent(activePromotion.productName)}`)}
        >
          <Box sx={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.08)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', bottom: -40, right: 60, width: 140, height: 140, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.06)', pointerEvents: 'none' }} />

          <Box sx={{ mb: 1 }}>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                background: '#E53935',
                color: '#fff',
                borderRadius: '4px',
                fontSize: 9,
                fontWeight: 700,
                px: 1.25,
                py: 0.375,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              NUEVO
            </Box>
          </Box>

          <Typography variant="h2" sx={{ color: '#fff', mb: 2.5 }}>
            {activePromotion.productName}
          </Typography>

          <Box sx={{ width: 40, height: 3, borderRadius: 2, background: 'linear-gradient(90deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.2) 100%)', mb: 2.5 }} />

          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, maxWidth: 340, lineHeight: 1.6, mb: 3 }}>
            {activePromotion.description}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={<Search sx={{ fontSize: 16 }} />}
              onClick={(e) => {
                e.stopPropagation();
                navigate('/doctor/medications');
              }}
              sx={{
                bgcolor: BLUE,
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: 13,
                py: 1.25,
                px: 2.5,
                textTransform: 'none',
                boxShadow: '0 4px 16px rgba(0,102,204,0.4)',
                '&:hover': { bgcolor: '#0059B3' },
              }}
            >
              {t('doctor.dashboard.searchCta')}
            </Button>
            <Button
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/doctor/medications?q=${encodeURIComponent(activePromotion.productName)}`);
              }}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: 13,
                py: 1.25,
                px: 2.5,
                textTransform: 'none',
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              }}
            >
              Ver más →
            </Button>
          </Box>

          {activePromotions.length > 1 && (
            <Box sx={{ display: 'flex', gap: 0.75, mt: 2.5 }}>
              {activePromotions.map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: i === promoIndex ? 16 : 6,
                    height: 6,
                    borderRadius: 3,
                    bgcolor: i === promoIndex ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.25)',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            borderRadius: 4,
            bgcolor: NAVY,
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            p: { xs: '28px 24px', sm: '36px 40px' },
            mb: 3,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', bottom: -40, right: 60, width: 140, height: 140, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.05)', pointerEvents: 'none' }} />

          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 500, mb: 0.5 }}>
            {greeting}
          </Typography>
          <Typography variant="h2" sx={{ color: '#fff', mb: 2.5 }}>
            {doctorName ? `Dr. ${doctorName}` : 'Dr.'}
          </Typography>

          <Box sx={{ width: 40, height: 3, borderRadius: 2, background: 'linear-gradient(90deg, #0066CC 0%, #00A86B 100%)', mb: 2.5 }} />

          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, maxWidth: 340, lineHeight: 1.6, mb: 3 }}>
            Consulta disponibilidad de medicamentos en farmacias asociadas.
          </Typography>

          <Button
            variant="contained"
            startIcon={<Search sx={{ fontSize: 16 }} />}
            onClick={() => navigate('/doctor/medications')}
            sx={{
              bgcolor: BLUE,
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: 13,
              py: 1.25,
              px: 2.5,
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(0,102,204,0.4)',
              '&:hover': { bgcolor: '#0059B3' },
            }}
          >
            {t('doctor.dashboard.searchCta')}
          </Button>
        </Box>
      )}

      {/* ── Stat Row ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 0.5,
          mb: 3,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <StatCard
          value={stats?.totalProducts}
          label="Total Medicamentos"
          icon={<MedicalServices />}
          loading={isLoading}
        />
        <StatCard
          value={stats?.totalInStock}
          label="En Stock"
          icon={<Inventory />}
          loading={isLoading}
        />
        <StatCard
          value={stats?.totalBranches}
          label="Sucursales Activas"
          icon={<Store />}
          loading={isLoading}
        />
      </Box>

      {isError && (
        <Typography sx={{ color: 'error.main', fontSize: 13, mb: 3 }}>
          Error al cargar estadísticas.
        </Typography>
      )}

      {/* ── Recently Added ────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: NAVY }}>
            Agregados Recientemente
          </Typography>
          <Button
            endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
            onClick={() => navigate('/doctor/medications')}
            size="small"
            sx={{ fontSize: 12, textTransform: 'none', color: BLUE, fontWeight: 600, p: 0, minWidth: 0 }}
          >
            Ver más
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            overflowX: 'auto',
            pb: 1,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  sx={{ flex: '0 0 200px', height: 140, borderRadius: 3 }}
                />
              ))
            : stats?.recentProducts.map((p) => (
                <MedCard
                  key={p.id}
                  product={p}
                  onClick={() => { const firstWord = p.name.split(' ')[0] || p.name; navigate(`/doctor/medications?q=${encodeURIComponent(firstWord)}`); }}
                />
              ))}
        </Box>
      </Box>

      {/* ── Recent Searches ───────────────────────────────────────────── */}
      <Card
        elevation={0}
        sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', p: 2.5, mb: 3 }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: NAVY, mb: 1.5 }}>
          Búsquedas Recientes
        </Typography>
        {recentSearches.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled', fontStyle: 'italic' }}>
            No hay búsquedas recientes.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {recentSearches.map((term) => (
              <Chip
                key={term}
                label={term}
                icon={<Search sx={{ fontSize: '14px !important' }} />}
                onClick={() => navigate(`/doctor/medications?q=${encodeURIComponent(term)}`)}
                size="small"
                sx={{
                  fontSize: 12,
                  bgcolor: 'rgba(0,102,204,0.07)',
                  color: BLUE,
                  fontWeight: 500,
                  border: '1px solid rgba(0,102,204,0.15)',
                  '&:hover': { bgcolor: 'rgba(0,102,204,0.13)' },
                }}
              />
            ))}
          </Box>
        )}
      </Card>

    </Box>
  );
};
